/**
 * usePermissions — what the signed-in person may do in the open business.
 *
 *   const { can, ready } = usePermissions();
 *   if (can("expenditure.approve")) …
 *   <Can permission="product.create">…</Can>
 *
 * Comes from GET /permissions/mine (their roles + any one-off grants,
 * scoped to the business they have open). Cached per person + business on
 * the device so menus don't flash on reload.
 *
 * Fails OPEN on the screen: if the list can't be loaded (old server, no
 * internet) `can()` falls back to the role, so nobody loses their menu. The
 * server still checks every request — hiding a button is a convenience,
 * never the security.
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import useAuthStore from "@/stores/authStore";
import useBusinessStore from "@/stores/businessStore";

export type Level = "tenant_owner" | "super_admin" | "business_admin" | "staff" | "cashier";

interface MinePayload {
  keys: string[];
  businessId: string | null;
  branchId?: string | null;
  role?: string;
  /** Their level HERE (business + branch) — a manager at one branch can be a cashier at another. */
  level?: Level | null;
  denied?: string;
}

const ADMIN_ROLES = new Set(["tenant_owner", "super_admin", "business_admin", "admin"]);
const cacheKey = (userId: string, biz: string | null) => `eaze:perms:${userId}:${biz ?? "tenant"}`;

function readCache(k: string): { keys: string[]; level?: Level | null } | undefined {
  try {
    const v = localStorage.getItem(k);
    if (!v) return undefined;
    const parsed = JSON.parse(v) as string[] | { keys: string[]; level?: Level | null };
    return Array.isArray(parsed) ? { keys: parsed } : parsed;
  } catch {
    return undefined;
  }
}

export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const biz = useBusinessStore((s) => s.activeBusinessId) ?? (user as { businessId?: string } | null)?.businessId ?? null;
  const userId = user?.id ?? "";
  // Branch roles only count at their branch, so the branch is part of the answer.
  const branch = useBusinessStore((s) => (biz ? s.activeBranchByBusiness?.[biz] ?? null : null));
  const key = cacheKey(userId, branch ? `${biz}:${branch}` : biz);

  const q = useQuery({
    queryKey: ["permissions", "mine", userId, biz, branch],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    retry: 1,
    initialData: () => {
      const c = readCache(key);
      return c ? { keys: c.keys, level: c.level ?? null, businessId: biz } : undefined;
    },
    initialDataUpdatedAt: 0, // always refresh the cached copy
    queryFn: async () => {
      const res = await api.get<{ data: MinePayload }>(
        "/permissions/mine",
        biz ? { headers: { "X-Business-Id": biz, ...(branch ? { "X-Branch-Id": branch } : {}) } } : undefined,
      );
      const data = res.data?.data ?? { keys: [], businessId: biz };
      try {
        localStorage.setItem(key, JSON.stringify({ keys: data.keys, level: data.level ?? null }));
      } catch {
        /* ignore */
      }
      return data;
    },
  });

  const keys = q.data?.keys;
  // An empty list from an old server (the M1 stub) means "not known yet".
  const known = Array.isArray(keys) && keys.length > 0;
  const set = new Set(keys ?? []);
  const role = user?.role as string | undefined;

  /** True when the person holds the permission. Unknown → decided by role. */
  const can = (perm: string | string[], fallback?: "admin" | "any"): boolean => {
    const list = Array.isArray(perm) ? perm : [perm];
    if (known) return list.some((p) => set.has(p));
    if (!role) return false;
    return fallback === "any" ? true : fallback === "admin" ? ADMIN_ROLES.has(role) : true;
  };

  // Level where they're working now; falls back to the account role.
  const level = ((q.data as MinePayload | undefined)?.level || role || null) as Level | null;

  return { can, ready: known || q.isFetched, keys: set, isLoading: q.isLoading && !known, level, levelKnown: !!(q.data as MinePayload | undefined)?.level };
}

export default usePermissions;
